import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
// import { perfMonitor } from './utils/performanceMonitor';
import { queryClient } from './lib/queryClient';
import { apiUrl } from './config/environment';

// Components that need to be loaded immediately
import SideNavbar from './components/SideNavbar';
import MobileNavigation from './components/MobileNavigation';
import AuthLanding from './pages/AuthLanding';
import { createLazyComponent, useChunkErrorHandler } from './utils/withChunkErrorBoundary';
import ShopPicksFallback from './components/fallbacks/ShopPicksFallback';
import UsernameErrorBoundary from './components/UsernameErrorBoundary';
import PurchaseOptionsFallback from './components/fallbacks/PurchaseOptionsFallback';
import { register as registerSW, preloadCriticalChunks } from './utils/serviceWorkerRegistration';
import { errorTracker, setUserId } from './utils/errorTracking';
import { initializePreloading, addResourceHints } from './utils/bundleOptimization';

// Lazy load heavy pages with error boundaries
const MySubscriptions = createLazyComponent(() => import('./pages/MySubscriptions'));
const MyPicks = createLazyComponent(() => import('./pages/MyPicks'));
const Dashboard = createLazyComponent(() => import('./pages/Dashboard'));
const CreatePick = createLazyComponent(() => import('./pages/CreatePick'));
const ShopPicks = createLazyComponent(() => import('./pages/ShopPicksOptimized'), ShopPicksFallback);
const ManageSubscriptions = createLazyComponent(() => import('./pages/ManageSubscriptions'));
const CreatorPromotions = createLazyComponent(() => import('./pages/CreatorPromotions'));
const PurchaseOptions = createLazyComponent(() => import('./pages/PurchaseOptions'), PurchaseOptionsFallback);
const PaymentSuccess = createLazyComponent(() => import('./pages/PaymentSuccess'));
const EditProfile = createLazyComponent(() => import('./pages/EditProfile'));
const Alerts = createLazyComponent(() => import('./pages/Alerts'));
const PrivacyPolicy = createLazyComponent(() => import('./components/PrivacyPolicy'));
const TermsOfService = createLazyComponent(() => import('./components/TermsOfService'));
const Support = createLazyComponent(() => import('./components/Support'));
const LockerRoom = createLazyComponent(() => import('./pages/LockerRoom'));
const SportHub = createLazyComponent(() => import('./pages/SportHub'));
const EmailVerification = createLazyComponent(() => import('./pages/EmailVerification'));
const CreatorOnboardingComplete = createLazyComponent(() => import('./pages/CreatorOnboardingComplete'));
const CreatorPayments = createLazyComponent(() => import('./pages/CreatorPayments'));
const CreatorNotifications = createLazyComponent(() => import('./pages/CreatorNotifications'));
const CreatorTerms = createLazyComponent(() => import('./pages/CreatorTerms'));
const ResetPassword = createLazyComponent(() => import('./pages/ResetPassword'));

// Admin pages (lazy loaded for zero impact on non-admin users)
const AdminLogin = createLazyComponent(() => import('./pages/AdminLogin'));
const AdminDashboard = createLazyComponent(() => import('./pages/AdminDashboard'));
const AdminProtectedRoute = createLazyComponent(() => import('./components/AdminProtectedRoute'));

// Component to redirect username URLs to the purchase page
function UsernameRedirect() {
  const { username } = useParams<{ username: string }>();
  const [loading, setLoading] = useState(true);
  const [creatorId, setCreatorId] = useState<string | null>(null);

  useEffect(() => {
    const fetchCreatorId = async () => {
      if (!username) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/creator/${username}`);

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setCreatorId(data.creator.id);
          }
        }
      } catch (error) {
        console.error('Error fetching creator:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreatorId();
  }, [username]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-green"></div>
      </div>
    );
  }

  if (creatorId) {
    return <Navigate to={`/shop-picks/purchase/${creatorId}`} replace />;
  }

  // If username not found, redirect to marketplace
  return <Navigate to="/marketplace" replace />;
}

function AppContent() {
  const { isCollapsed } = useSidebar();
  const { signOut } = useAuth();
  const [user, setUser] = useState<any>(null);
  const location = useLocation();

  // Handle chunk loading errors globally
  useChunkErrorHandler();

  // Function to check and update user state
  const checkUserState = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    } else {
      setUser(null);
    }
  };

  // Check user state on mount
  useEffect(() => {
    checkUserState();
  }, []);

  // Listen for storage changes (when user logs in/out in another tab)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        checkUserState();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Listen for custom events (when user logs in/out in same tab)
  useEffect(() => {
    const handleUserChange = () => {
      checkUserState();
    };

    window.addEventListener('userStateChanged', handleUserChange);
    return () => window.removeEventListener('userStateChanged', handleUserChange);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback to manual cleanup if Supabase signOut fails
      localStorage.removeItem('user');
      setUser(null);
      window.dispatchEvent(new Event('userStateChanged'));
    }
  };

  useEffect(() => {
    // perfMonitor.start('App.initialization');

    // Initialize all performance and reliability systems
    const initializeSystems = async () => {
      try {
        // Add resource hints
        addResourceHints();

        // Register service worker
        registerSW({
          onSuccess: () => console.log('Service Worker registered successfully'),
          onUpdate: () => console.log('Service Worker updated'),
          onOffline: () => console.log('App is running offline'),
          onOnline: () => console.log('App is back online')
        });

        // Preload critical chunks
        preloadCriticalChunks();

        // Initialize preloading based on user behavior
        initializePreloading();

        // Setup performance tracking (disabled)
        // perfMonitor.trackWebVitals();
        // perfMonitor.trackNavigationTiming();

        // Set user ID for error tracking if available
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          setUserId(userData.id || userData.email);
        }

        // Make error tracker globally available
        (window as any).errorTracker = errorTracker;

      } catch (error) {
        console.error('Failed to initialize systems:', error);
      }
    };

    // Track initial app load performance
    const handleLoad = () => {
      // perfMonitor.end('App.initialization');
      // perfMonitor.logSummary();
    };

    if (document.readyState === 'complete') {
      handleLoad();
      initializeSystems();
    } else {
      window.addEventListener('load', () => {
        handleLoad();
        initializeSystems();
      });
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);
  
  // Check if we're on auth pages (landing, verification, or reset password)
  const isAuthFlow = location.pathname === '/auth' || location.pathname === '/verify-email' || location.pathname === '/reset-password';

  return (
    <div className="min-h-screen gradient-dark flex">
      {/* Conditionally render navigation - hide on auth pages */}
      {!isAuthFlow && (
        <>
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <SideNavbar />
          </div>

          {/* Mobile Navigation */}
          <MobileNavigation user={user} onLogout={handleLogout} />
        </>
      )}


      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${
        !isAuthFlow && isCollapsed ? 'md:ml-16' : !isAuthFlow ? 'md:ml-64' : ''
      } flex flex-col`}>

        {/* Main Content - with bottom padding for mobile navigation only when navbar is shown */}
        <div className={`flex-1 ${!isAuthFlow ? 'pb-20 md:pb-0' : ''}`}>
          <Suspense fallback={
            <div className="flex items-center justify-center h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-green"></div>
            </div>
          }>
            {/*
              IMPORTANT: Route order matters!
              - More specific routes must come before less specific ones
              - The /:username route MUST be the last route as it catches any path
              - Adding new routes? Place them BEFORE the /:username route
              - Static routes (like /marketplace, /admin-dashboard) should come before dynamic routes
            */}
            <Routes>
          <Route path="/" element={<Navigate to="/shop-picks" replace />} />
          <Route path="/marketplace" element={<ShopPicks />} />
          <Route path="/my-picks" element={<MyPicks />} />
          <Route path="/auth" element={<AuthLanding />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Backward compatibility redirects */}
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/signup" element={<Navigate to="/auth" replace />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/create-pick" element={<ProtectedRoute requireCreator><CreatePick /></ProtectedRoute>} />
          <Route path="/creator-promotions" element={<ProtectedRoute requireCreator><CreatorPromotions /></ProtectedRoute>} />
          <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
          <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
          <Route path="/shop-picks" element={<ShopPicks />} />
          <Route path="/shop-picks/purchase/:creatorId" element={<PurchaseOptions />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/success" element={<PaymentSuccess />} />
          <Route path="/manage-subscriptions" element={<ProtectedRoute><ManageSubscriptions /></ProtectedRoute>} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/support" element={<Support />} />
          <Route path="/locker-room" element={<LockerRoom />} />
          <Route path="/locker-room/:sport" element={<LockerRoom />} />
          <Route path="/sport-hub/:sport" element={<SportHub />} />
          <Route path="/creator/onboarding/complete" element={<CreatorOnboardingComplete />} />
          <Route path="/creator/payments" element={<ProtectedRoute requireCreator><CreatorPayments /></ProtectedRoute>} />
          <Route path="/creator/notifications" element={<ProtectedRoute requireCreator><CreatorNotifications /></ProtectedRoute>} />
          <Route path="/creator-terms" element={<CreatorTerms />} />

          {/* Admin routes - lazy loaded, zero impact on non-admin users */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />

          {/*
            ⚠️  CRITICAL: Username redirect route - MUST be last! ⚠️
            This route catches ANY path that doesn't match the routes above.
            Adding ANY route after this will make it unreachable!
            New routes must be added ABOVE this line.
          */}
          <Route path="/:username" element={
            <UsernameErrorBoundary fallbackPath="/marketplace">
              <UsernameRedirect />
            </UsernameErrorBoundary>
          } />
          </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <SidebarProvider>
            <AppContent />
          </SidebarProvider>
        </AuthProvider>
      </Router>
      <Analytics />
    </QueryClientProvider>
  );
}

export default App; 